import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface PreviewImageProps {
  status: 'idle' | 'pending' | 'ok' | 'error'
  blobUrl: string | null
  error: { code: string; message: string } | null
  /**
   * Print-time rotation the pipeline applied to this raster (issue #133):
   * 0 = unrotated, 90/270 = the ZPL body was generated in PHYSICAL
   * (rotated) orientation — see `resolvePrintRotation`. The `<img>` itself
   * always shows the raw (physically-oriented) raster; when non-zero we
   * counter-rotate it back to DESIGN orientation so the preview matches
   * what the operator drew, while the print output is unaffected. Defaults
   * to 0 (identical to pre-#133 behavior).
   */
  rotationDeg?: 0 | 90 | 270
}

const NON_RECOVERABLE_CODES = new Set(['ZPL_INVALID', 'UNAUTHORIZED'])

/**
 * 'fit'  — image scales DOWN to the visible pane (aspect preserved, never
 *          upscaled past native size); the whole label is visible, no scroll.
 * 'full' — native 1px=1dot size with scrolling. Click the image to toggle.
 */
type ZoomMode = 'fit' | 'full'

export function PreviewImage({ status, blobUrl, error, rotationDeg = 0 }: PreviewImageProps) {
  const { t } = useTranslation()
  const [zoom, setZoom] = useState<ZoomMode>('fit')
  const isRedError = error !== null && NON_RECOVERABLE_CODES.has(error.code)
  const bannerClass = isRedError
    ? 'border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100'
    : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100'

  const showImg = blobUrl !== null && !isRedError && status !== 'idle'

  // Measured AFTER the browser decodes the actual PNG bytes — deliberately
  // never trusts the render response's widthPx/heightPx (those are
  // unmeasured arithmetic on the render-backend side, stale whenever
  // rotation applies; see recon).
  //
  // Captures `rotationDeg` ALONGSIDE the measurement (round-1 review,
  // major finding): `rotationDeg` is driven synchronously off
  // template/profile (PreviewPane's useMemo) while `blobUrl` lags behind
  // an async render round trip (generateZpl+lint await, then useRender's
  // debounce, then the fetch). If we counter-rotated the STILL-DISPLAYED
  // raster by whatever `rotationDeg` prop happens to be current, an edit
  // that flips resolvePrintRotation's decision would immediately apply the
  // NEW rotation to the OLD (correctly-oriented-for-the-OLD-rotation)
  // raster — a transient, wrongly-rotated preview. Freezing the rotation
  // used for the transform to whatever was in effect when THIS raster
  // loaded, and only trusting it while it still names the CURRENT blobUrl,
  // means a rotationDeg change alone (no new blobUrl) is a no-op until a
  // fresh raster actually arrives and reloads.
  const [rendered, setRendered] = useState<{
    blobUrl: string
    rotationDeg: 0 | 90 | 270
    width: number
    height: number
  } | null>(null)

  // Only counter-rotate once we have a real measurement of the CURRENTLY
  // displayed raster to swap — until then (or once blobUrl has moved on
  // without a fresh load yet) the plain (physically-oriented) image shows,
  // same as pre-#133. Bound to a single narrowed local so every usage below
  // type-narrows `rendered` without a separate non-null assertion per site.
  const rotationBox =
    rendered !== null && rendered.blobUrl === blobUrl && rendered.rotationDeg !== 0
      ? rendered
      : null
  const rotated = rotationBox !== null

  return (
    <div className="flex h-full min-h-0 flex-col items-center justify-center gap-2 overflow-hidden bg-stone-100 p-3 dark:bg-stone-950">
      {error && (
        <div
          data-testid="preview-banner"
          className={`w-full rounded border px-2 py-1 text-xs ${bannerClass}`}
        >
          {error.message} ({error.code})
        </div>
      )}
      {status === 'pending' && (
        <p data-testid="preview-pending" className="text-[11px] text-stone-500 dark:text-stone-400">
          {t('preview.rendering')}
        </p>
      )}
      {status === 'idle' && !error && (
        <p data-testid="preview-idle" className="text-[11px] text-stone-500 dark:text-stone-400">
          {t('preview.idle')}
        </p>
      )}
      {showImg && blobUrl && (
        <div data-testid="preview-zoom-region" className="relative min-h-0 w-full flex-1">
          {/*
            `flex` + `m-auto` on the img centers it in BOTH modes and — unlike
            justify-center/items-center — keeps the start edges reachable when
            the native-size image overflows the scroll container in 'full' mode.
          */}
          <div
            data-testid="preview-img-scroll"
            className={`flex h-full w-full ${zoom === 'fit' ? 'overflow-hidden' : 'overflow-auto'}`}
          >
            {/*
              image-rendering is left at `auto` (smooth) deliberately: the PNG is
              1px=1dot, and at non-integer downscales crisp-edges/pixelated alias
              barcode bars (bars drop out or double). Smooth interpolation keeps
              the barcode readable as a preview; 100% mode shows exact dots.
              max-w/max-h 100% only shrinks — a label smaller than the pane stays
              at native size (never upscaled past 100%).

              `preview-rotate-box` is a permanent wrapper (always the img's
              parent, `display:contents` — i.e. layout-transparent — when not
              rotated) so the <img> never remounts across the rotated ⇄
              unrotated transition once it loads; only its class/style flips.
              When rotated, the box is sized to the DESIGN aspect ratio (the
              natural W×H measured on load, swapped) and the <img> is
              counter-rotated back inside it — see the PreviewImageProps
              rotationDeg doc comment.

              `containerType: 'size'` turns the box into a CSS query
              container (round-1 review, blocker fix): the box itself is
              already correctly clamped to the pane via max-w-full/max-h-full
              + aspectRatio, but the <img> below used to be sized in FIXED
              PHYSICAL PIXELS — ignoring that clamp entirely, so a raster
              bigger than the pane rendered oversized and got clipped by the
              overflow-hidden scroll ancestor to an unrecognizable sliver in
              'fit' mode. Sizing the <img> in `cqh`/`cqw` (container-query %,
              swapped — see below) instead ties it to the box's ACTUAL
              (possibly clamped) rendered size, so it always scales down
              together with the box. In 100% zoom the box is unclamped
              (native size), so the same formula naturally reduces to the
              old fixed-px result — verified in a real Chromium layout for
              both cases (jsdom performs no CSS layout and can't observe this).
            */}
            <div
              data-testid="preview-rotate-box"
              className={
                rotated
                  ? `relative m-auto${zoom === 'fit' ? ' max-h-full max-w-full' : ''}`
                  : 'contents'
              }
              style={
                rotationBox
                  ? {
                      width: `${rotationBox.height}px`,
                      aspectRatio: `${rotationBox.height} / ${rotationBox.width}`,
                      containerType: 'size',
                    }
                  : undefined
              }
            >
              <img
                data-testid="preview-img"
                src={blobUrl}
                alt="Printer preview"
                role="button"
                tabIndex={0}
                aria-pressed={zoom === 'full'}
                aria-label={zoom === 'fit' ? t('preview.zoom.hintToFull') : t('preview.zoom.hintToFit')}
                title={zoom === 'fit' ? t('preview.zoom.hintToFull') : t('preview.zoom.hintToFit')}
                onClick={() => setZoom(z => (z === 'fit' ? 'full' : 'fit'))}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setZoom(z => (z === 'fit' ? 'full' : 'fit'))
                  }
                }}
                onLoad={e => {
                  const { naturalWidth, naturalHeight } = e.currentTarget
                  if (!blobUrl) return
                  setRendered({ blobUrl, rotationDeg, width: naturalWidth, height: naturalHeight })
                }}
                style={
                  rotationBox
                    ? {
                        // Swapped axes so the post-rotation bounding box
                        // exactly fills the (possibly clamped) query
                        // container with no distortion — see the box's
                        // containerType comment above.
                        width: '100cqh',
                        height: '100cqw',
                        transform: `translate(-50%, -50%) rotate(${-rotationBox.rotationDeg}deg)`,
                      }
                    : undefined
                }
                className={
                  rotated
                    ? `absolute left-1/2 top-1/2 rounded border border-stone-300 bg-white dark:border-stone-700 ${
                        zoom === 'fit' ? 'cursor-zoom-in' : 'cursor-zoom-out'
                      }`
                    : `m-auto rounded border border-stone-300 bg-white dark:border-stone-700 ${
                        zoom === 'fit'
                          ? 'max-h-full max-w-full cursor-zoom-in object-contain'
                          : 'max-w-none cursor-zoom-out'
                      }`
                }
              />
            </div>
          </div>
          <span
            data-testid="preview-zoom-indicator"
            className="pointer-events-none absolute right-2 top-2 rounded bg-stone-900/70 px-1.5 py-0.5 text-[10px] font-medium text-white dark:bg-stone-100/80 dark:text-stone-900"
          >
            {zoom === 'fit' ? t('preview.zoom.fit') : t('preview.zoom.full')}
          </span>
        </div>
      )}
    </div>
  )
}
